/**
 * PREDICTIVE SESSION — Go Online/Offline for agents on Phonomenal's OWN
 * native predictive-dialer engine (2026-09-14, New_NC_1 rollout).
 *
 * Different mechanism from the progressive dialer (StartDialerButton /
 * CohortStartDialer): those are OUR backend deciding when to dial and opening
 * a Phonomenal session as a side effect of each dial (see voizDialer.js's
 * header comment). The predictive engine instead decides FOR ITSELF when to
 * ring someone, based on who already holds an open, idle session — nothing
 * else in the app opens one proactively. This button is that missing
 * "log in and wait" / "log out" step.
 *
 * Self-gates via GET /voice/predictive/status — renders null for every agent
 * except those with admins.predictiveDialer:true (server-enforced on the
 * start/stop routes too, this is just so the button doesn't clutter
 * everyone else's header).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button, Tooltip, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk';
import PhoneDisabledIcon from '@material-ui/icons/PhoneDisabled';
import usePredictiveStatus from '../../../hooks/usePredictiveStatus';

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();

// Every state the agent can be in, and whether the engine can ring them.
// GREEN = calls will land. AMBER/RED = they are invisible to the dialler and
// the label says why — the whole point, since an agent in `manual` or with no
// session previously showed a plain green "Online".
const STATE = {
  ready:            { label: 'Predictive: Ready',        color: '#2e7d32', border: '#a5d6a7', bg: '#e8f5e9', help: 'Waiting for the next call.' },
  on_call:          { label: 'Predictive: On Call',      color: '#4527a0', border: '#b39ddb', bg: '#ede7f6', help: 'You are on a call.' },
  ringing:          { label: 'Predictive: Ringing…',     color: '#4527a0', border: '#b39ddb', bg: '#ede7f6', help: 'A call is being offered to you.' },
  wrapping_up:      { label: 'Predictive: Wrapping Up',  color: '#4527a0', border: '#b39ddb', bg: '#ede7f6', help: 'Submit the wrap-up form — the next call comes after it.' },
  manual:           { label: 'Manual — not receiving',   color: '#e65100', border: '#ffcc80', bg: '#fff4e5', help: 'You are in manual mode, so the dialler cannot ring you. Click to go back on.' },
  no_session:       { label: 'No session — click to fix', color: '#e65100', border: '#ffcc80', bg: '#fff4e5', help: 'Your softphone is up but no dialler session is open. Click to open one.' },
  softphone_offline:{ label: 'Softphone offline',        color: '#c62828', border: '#ef9a9a', bg: '#fdecea', help: 'Log into the VOIZ softphone first, then click here.' },
  logged_in:        { label: 'Go Online (Predictive)',   color: '#616161', border: '#bdbdbd', bg: '#f5f5f5', help: 'Start receiving predictive-dialer calls.' },
  offline:          { label: 'Go Online (Predictive)',   color: '#616161', border: '#bdbdbd', bg: '#f5f5f5', help: 'Start receiving predictive-dialer calls.' },
};

export default function PredictiveSessionButton() {
  // enabled/online/uiState now come from the shared poller (usePredictiveStatus)
  // instead of this component running its own 15s interval — see that hook's
  // header comment for why (4 components independently polling the same
  // endpoint was the confirmed driver of the prelive CPU pin).
  const [st, checked, refresh] = usePredictiveStatus();
  const enabled = !!st?.enabled;
  // ── THE REAL STATE, NOT A BOOLEAN (2026-09-21) ────────────────────────
  // This used to be `status !== 'none'`, which called an agent in MANUAL
  // "online" — green button, and the predictive engine unable to ring them.
  // Same for an agent whose softphone is up but who holds no session. Both
  // were live on 2026-09-21 and each had to be found by hand.
  // uiState comes from the backend: ready | manual | no_session |
  // softphone_offline | on_call | wrapping_up | ringing | logged_in | offline
  const uiState = st?.uiState || 'offline';
  const online = !!st?.online; // TRULY ready — engine can ring them
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const goOnline = () => {
    setBusy(true);
    axios.post(`${AI_PATH}/api/ai/voice/predictive/session/start`, {})
      .then((r) => {
        if (r.data?.success) {
          setToast({ open: true, severity: 'success', message: 'You are Online — predictive calls will ring your softphone.' });
        } else {
          setToast({ open: true, severity: 'error', message: r.data?.message || 'Could not go online.' });
        }
      })
      .catch((e) => {
        const code = e.response?.data?.code;
        // ⚠️ The button must NOT go green here. STUCK_ON_MANUAL means the
        // server tried dispose_call, switch_to_auto AND a full member reset and
        // the line still reports a call in progress — the agent is not online,
        // and showing them as online is how they end up sitting idle believing
        // the engine will ring them (Hariom, ext 2953, 2026-09-23).
        const msg = code === 'SOFTPHONE_OFFLINE'
          ? 'Log into the VOIZ softphone first, then go online.'
          : code === 'STUCK_ON_MANUAL'
            ? (e.response?.data?.message || 'Still on manual — hang up any open call and try again.')
            : (e.response?.data?.message || e.message);
        setToast({ open: true, severity: 'error', message: msg });
      })
      .finally(() => { setBusy(false); refresh(); });
  };

  const goOffline = () => {
    setBusy(true);
    axios.post(`${AI_PATH}/api/ai/voice/predictive/session/stop`, {})
      .then(() => {
        setToast({ open: true, severity: 'success', message: 'You are Offline — you will not receive predictive calls.' });
      })
      .catch((e) => setToast({ open: true, severity: 'error', message: e.response?.data?.message || e.message }))
      .finally(() => { setBusy(false); refresh(); });
  };

  if (!checked || !enabled) return null;

  const needsFix = ['manual', 'no_session', 'softphone_offline'].includes(uiState);

  return (
    <>
      {/* Same vocabulary as Voice -> Campaign -> Assigned Agents, so an agent
          and their TL read the same word for the same state. A state that
          BLOCKS calls is amber/red and says what to do about it. */}
      <Tooltip title={STATE[uiState] ? STATE[uiState].help : 'Predictive dialer'}>
        <span>
          <Button
            // manual / no_session are NOT online — clicking must FIX them, not
            // log the agent out. Only a genuinely ready agent gets Go Offline.
            onClick={needsFix ? goOnline : (online ? goOffline : goOnline)}
            disabled={busy}
            variant="outlined"
            startIcon={online ? <PhoneInTalkIcon /> : <PhoneDisabledIcon />}
            style={{
              marginRight: 12, textTransform: 'none', fontWeight: 700, fontSize: 13,
              padding: '6px 14px', borderRadius: 8,
              color: (STATE[uiState] || STATE.offline).color,
              borderColor: (STATE[uiState] || STATE.offline).border,
              background: (STATE[uiState] || STATE.offline).bg,
              whiteSpace: 'nowrap',
            }}
          >
            {(STATE[uiState] || STATE.offline).label}
          </Button>
        </span>
      </Tooltip>

      <Snackbar open={toast.open} autoHideDuration={6000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>{toast.message}</Alert>
      </Snackbar>
    </>
  );
}
